import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { MenuItemsApiService } from '../../services/menu-items-api.service';
import { DOCUMENT } from '@angular/common';
import { UserHeaderInfo } from '../user-header-info/user-header-info.component';
import { AuthService, IdToken, User } from '@auth0/auth0-angular';
import { deepEqual } from 'src/app/shared/utils/global.utils';
import { combineLatest, filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MetricsService } from 'src/app/shared/services/metrics/metrics.service';
import { MatIcon } from '@angular/material/icon';
import { BlocksModule, MenuRiService } from '@r1-development/rps-shared-ui';
import { MatToolbar } from '@angular/material/toolbar';

@Component({
  selector: 'ri-resolution-ui-header',
  imports: [MatIcon, BlocksModule],
  templateUrl: './header.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnInit {
  private readonly metricsService = inject(MetricsService);

  // Reference variables
  //private readonly menubarRef = viewChild<MatToolbar>('menubarRef');

  // Signals
  private readonly _userInfo = signal<UserHeaderInfo | null>(null);
  public readonly userInfo = computed(
    () => {
      return this._userInfo();
    },
    {
      equal: (a: UserHeaderInfo | null, b: UserHeaderInfo | null) => {
        return deepEqual(a, b);
      },
    }
  );

  // Dependencies
  private readonly menuItemsApiService = inject(MenuItemsApiService);
  private readonly authService = inject(AuthService);
  private readonly document: Document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly sharedMenuService = inject(MenuRiService);

  public readonly metrics = computed(() => {
    return this.metricsService.metricsSignal();
  });

  // Other
  private readonly window = this.document.defaultView!; // Asserted in constructor

  public readonly customUserActions = [
    {
      label: 'View Profile',
      routerLink: '/my-profile',
    },
  ];

  constructor() {
    if (!this.window) {
      throw new Error(
        'window object is null, check environment where code is run'
      );
    }
    this.loadMenuFromService();
  }

  ngOnInit() {
    this.extractUserDataFromAuthProvider();
    this.metricsService.loadMetrics();
  }

  public loadMenuFromService(): void {
    effect(() => {
      const apiMenuItems = this.menuItemsApiService.menuItems();
      untracked(() => {
        if (!apiMenuItems) {
          return;
        }
        this.sharedMenuService.setMenuItems(apiMenuItems);
      });
    });
  }

  /*onContainerMouseLeave(): void {
    const menubarRef = this.menubarRef();
    console.log(menubarRef);

    if (menubarRef) {
      menubarRef.hide();
    }
  }*/

  onLogout() {
    this.authService.logout({
      logoutParams: {
        returnTo: location.origin + '/logged-out',
        federated: false,
      },
    });
  }

  private extractUserDataFromAuthProvider() {
    combineLatest({
      user: this.authService.user$,
      idToken: this.authService.idTokenClaims$,
    })
      .pipe(
        filter<{
          user: User | null | undefined;
          idToken: IdToken | null | undefined;
        }>(({ user }: { user: User | null | undefined }) => !!user),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(
        ({
          user,
          idToken,
        }: {
          user: User | null | undefined;
          idToken: IdToken | null | undefined;
        }) => {
          this._userInfo.set({
            fullName: idToken?.['displayName'] || user!.name,
            email: user?.email,
          });
        }
      );
  }
}
